import { deviceApi } from '@/api/device/deviceApi';
import { type Device } from '@/models/device/Device';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from "../../theme";
import { useToast } from '../common/toast/ToastProvider';
import DeviceDetailDialog from './DeviceDetailDialog';

const DeviceList = () => {
    const qc = useQueryClient();
    const app = useAppContext();
    // No staleTime: the device list should refetch each time this screen is opened (fresh on every mount).
    const { data: deviceList, isPending: isGridLoading } = useQuery<Device[]>({ queryKey: ['devices', app.locationId], enabled: app.locationId !== null, queryFn: () => deviceApi.list(app.locationId!), refetchOnMount: 'always' });
    const { mutateAsync: toggleEnabled, isPending: isToggling } = useMutation({ mutationFn: deviceApi.toggleEnabled, onSuccess: () => handleToggleSuccess(), onError: () => handleToggleError() });
    const toast = useToast();
    const strings = useLocalizedStrings();
    const { canUpdate } = usePermissions();
    const canToggle = canUpdate(Perm.devices);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const selected = deviceList?.find((d) => d.deviceId === selectedId) ?? null;

    const columns: GridColDef<Device>[] = [
        {
            field: 'deviceId',
            headerName: strings.deviceId,
            flex: 2,
        },
        {
            field: 'manufacturer',
            headerName: strings.manufacturer,
            flex: 1,
        },
        {
            field: 'model',
            headerName: strings.model,
            flex: 2,
        },
        {
            field: 'osFriendlyVersion',
            headerName: strings.osFriendlyVersion,
            flex: 1,
        },
        {
            field: 'enabled',
            headerName: strings.enabled,
            type: 'boolean',
            flex: 1,
            sortable: true,
            renderCell: (params: GridRenderCellParams<Device, boolean>) => (
                <Switch
                    checked={params.row.enabled}
                    disabled={!canToggle || isToggling}
                    onChange={() => toggleEnabled(params.row.deviceId)}
                />
            ),
        },
        {
            field: 'actions', headerName: strings.actions, flex: 0.9, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<Device>) => (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => setSelectedId(params.row.deviceId)}
                    sx={{ fontSize: '0.78rem', py: 0.4, px: 1.5, minWidth: 0 }}
                >
                    {strings.viewDetails}
                </Button>
            ),
        },
    ];

    const handleToggleSuccess = async () => {
        await qc.invalidateQueries({ queryKey: ['devices'] });
    };

    const handleToggleError = () => {
        toast({ severity: "error", message: strings.deviceSaveError });
    };

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            {app.locationId === null ? (
                // With no location selected the query is disabled (which otherwise reads as a perpetual
                // loading grid) — prompt for a location instead.
                <Box sx={{ ...fillHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">{strings.devicesSelectLocation}</Typography>
                </Box>
            ) : (
                <DataGrid
                    getRowId={(row) => row.deviceId}
                    rows={deviceList}
                    columns={columns}
                    autoPageSize={true}
                    loading={isGridLoading}
                    localeText={{ noRowsLabel: strings.devicesNotFound }}
                />
            )}

            <DeviceDetailDialog open={selected !== null} device={selected} onClose={() => setSelectedId(null)} />
        </Box>
    );
};

export default DeviceList;
